import React, { useEffect } from 'react';
import { Controller, FormState, SubmitHandler, useForm } from 'react-hook-form';
import { LabelledSelect } from '../../../../components/LabelledSelect';
import { SelectOption } from '../../../../components/Select';
import { Col, Row } from 'reactstrap';
import { Chip, ChipColor } from '../../../../components/Chip';
import { JobPost } from '../../../../entities/applicant_tracking/JobPost';
import { CandidateResumeInput } from '../CandidateResumeInput';
import { CandidateCoverLetterInput } from '../CandidateCoverLetterInput';
import { CandidateDto } from '../../../../services/applicant_tracking/CandidateService/CandidateDto';
import { CandidateAcademicInput } from '../CandidateAcademicInput';
import { ApplicationSalaryInput } from '../ApplicationSalaryInput';
import { FormFieldHeader } from '../../../../components/FormFieldHeader';
import { SourceInput } from '../SourceInput';
import { CandidateCurrentPositionInput } from '../CandidateCurrentPositionInput';
import { CandidateYearsExperienceInput } from '../CandidateYearsExperienceInput';
import { CandidatePortfolioInput } from '../CandidatePortfolioInput';
import { CandidateDetailInput } from '../CandidateDetailInput';
import { Source } from '../../../../entities/applicant_tracking/Source';
import { HasWorkingVisaInput } from '../HasWorkingVisaInput';
import { handleFormErrors } from '../../../../services/ApiService/handler';
import { FormErrorMessage } from '../../../../components/FormErrorMessage';
import { ReferralInputData } from '../ReferralInput/ReferralInputData';
import { ReferralKnowledge } from '../ReferralInput/ReferralConsentKnowledge';
import JobStageSelect from '../../../../components/JobStageSelect';
import {
  getJobStageTriggers,
  TriggersIcon,
} from '../../../JobPage/PipelineTab/TriggersIcon';
import { Icon } from '../../../../components/Icon';
import { usePipelineJobStages } from '../../../../queries/v1/applicant_tracking/usePipelineJobStages';
import { useJobDetails } from '../../../../queries/useJobDetails';
import { CandidateDocumentsInput } from '../CandidateDocumentsInput';
import { ApplicationService } from '../../../../services/applicant_tracking/ApplicationService';

export const REFERRAL_VALUE = 'referral_not_actually_a_source';
const PRE_FILLED_CAPTION =
  "Filled from the candidate's most recent application.";

interface PropTypes {
  applicationDocumentsFf: boolean;
  candidateId: number;
  applicationId?: number;
  jobPosts: JobPost[] | null;
  sources: Source[] | null;
  appliedJobPostIds: number[];
  onSuccessfulSubmit: (applicationId: number) => void;
  headerFactory: (formState: FormState<CandidateDto>) => React.ReactNode;
  candidateDto?: Partial<CandidateDto>;
  referrerUserId?: number;
}
interface StageAutomationsLabelProps {
  jobId?: number;
  jobStageId?: number;
}

function StageAutomationsLabel({
  jobId,
  jobStageId,
}: StageAutomationsLabelProps) {
  const { data: pipelineJobStages = [], isLoading: isLoadingPipelineJob } =
    usePipelineJobStages(jobId);
  const { data: jobDetails, isLoading: isLoadingJobDetails } =
    useJobDetails(jobId);

  const isLoading = isLoadingPipelineJob || isLoadingJobDetails;

  if (isLoading) return null;

  const pipelineJob = pipelineJobStages.find(
    (pieplineJobStage) =>
      Number(pieplineJobStage.jobStageId) === Number(jobStageId),
  );

  if (!pipelineJob || !jobDetails) return null;

  const label = 'Automations are configured for this stage';
  const triggers = getJobStageTriggers(pipelineJob, jobDetails.triggers);

  return (
    <>
      {jobDetails && triggers && pipelineJob && triggers.length > 0 ? (
        <div className='d-flex align-items-center justify-content-end gap-2 mt-2'>
          <small className='d-flex mb-0 text-dark-200'>{label}</small>
          <TriggersIcon
            pipelineJobStage={pipelineJob}
            jobStageTriggers={triggers}
          >
            <Icon name={'bi-info-circle'} className={'text-blue-500'} />
          </TriggersIcon>
        </div>
      ) : null}
    </>
  );
}

export function ApplicationForm(props: PropTypes) {
  const {
    handleSubmit,
    control,
    register,
    setValue,
    getValues,
    watch,
    resetField,
    formState,
    setError,
  } = useForm<CandidateDto>({
    defaultValues: props.candidateDto,
    mode: 'onChange',
  });

  const values = getValues();

  const onSubmit: SubmitHandler<CandidateDto> = async (data: CandidateDto) => {
    try {
      const applicationId = await upsert(
        props.candidateId,
        props.applicationId!,
        data,
      );
      props.onSuccessfulSubmit(applicationId);
    } catch (e: unknown) {
      handleFormErrors(e, setError);
    }
  };

  const jobPostOptions =
    props.jobPosts?.map((jobPost) =>
      mapJobPostToOption(jobPost, props.appliedJobPostIds),
    ) || [];

  const jobPostId = watch('jobPostId');
  const jobStageId = watch('jobStageId');
  const jobPost = props.jobPosts?.find(
    (jobPost) => jobPost.id.toString() === jobPostId?.toString(),
  );

  const initSourceId =
    props.candidateDto?.referrerUserId ||
    props.candidateDto?.referrerEmail ||
    props.referrerUserId
      ? REFERRAL_VALUE
      : props.candidateDto?.sourceId;
  const watchResumeContent = watch('resume.content');
  const watchResumeFilename = watch('resume.filename');
  const watchResumeId = watch('resume.id');
  const watchCoverLetterFilename = watch('coverLetter.filename');
  const watchCoverLetterId = watch('coverLetter.id');
  const watchDocuments = watch('documents');

  const resumeCaptionText = watchResumeId ? PRE_FILLED_CAPTION : undefined;
  const coverLetterCaptionText = watchCoverLetterId
    ? PRE_FILLED_CAPTION
    : undefined;
  const documentsCaptionText = watchDocuments?.some((d) => d.id && !d.content)
    ? "Filled from the candidate's most recent application. Remove any you do not want to carry over."
    : undefined;

  useEffect(() => {
    setValue('sourceId', initSourceId);
  }, [initSourceId]);

  useEffect(() => {
    if (watchResumeContent == null) return;

    setValue('resume.id', null);
  }, [watchResumeContent]);

  useEffect(() => {
    if (watchResumeFilename != null) return;

    setValue('resume.id', null);
  }, [watchResumeFilename]);

  useEffect(() => {
    if (watchCoverLetterFilename != null) return;

    setValue('coverLetter.id', null);
  }, [watchCoverLetterFilename]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormErrorMessage error={formState.errors} />
      {props.headerFactory(formState)}
      <div className='d-flex flex-column gap-4'>
        <Row>
          <Col xs={12}>
            <Row>
              <Col xs={3}>
                <Controller
                  name='jobPostId'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <LabelledSelect
                      options={jobPostOptions}
                      selected={jobPostOptions?.find(
                        (option) => option.value === field.value?.toString(),
                      )}
                      label='Job'
                      onChange={(newOption) => {
                        field.onChange(newOption.value);
                        setValue('jobStageId', undefined);
                      }}
                      className='fs-5'
                      labelClassName='fw-semibold'
                      mandatory={true}
                      disabled={props.applicationId != null}
                      isLoading={props.jobPosts == null}
                    />
                  )}
                />
              </Col>
              {props.referrerUserId ? null : (
                <Col xs={3}>
                  <Controller
                    name='jobStageId'
                    control={control}
                    rules={{ required: !props.referrerUserId }}
                    render={({ field }) => (
                      <JobStageSelect
                        jobId={jobPost?.job?.id}
                        disabled={!!props.referrerUserId}
                        mandatory={!props.referrerUserId}
                        jobStageId={values.jobStageId}
                        onChange={(newOption) =>
                          field.onChange(newOption.value)
                        }
                      />
                    )}
                  />
                </Col>
              )}
            </Row>
          </Col>

          {props.referrerUserId ? null : (
            <Col xs={6}>
              <StageAutomationsLabel
                jobId={jobPost?.job?.id}
                jobStageId={jobStageId}
              />
            </Col>
          )}
        </Row>
        <CandidateResumeInput
          formRegister={register}
          getValues={getValues}
          setValue={setValue}
          captionText={resumeCaptionText}
        />
        <CandidateCoverLetterInput
          formRegister={register}
          getValues={getValues}
          setValue={setValue}
          captionText={coverLetterCaptionText}
        />
        {props.applicationDocumentsFf && (
          <CandidateDocumentsInput
            control={control}
            captionText={documentsCaptionText}
          />
        )}
        <CandidateAcademicInput formControl={control} />
        <ApplicationSalaryInput
          width={6}
          formHeaderClassNames={'fw-semibold mb-2'}
          formControl={control}
          watch={watch}
          formRegister={register}
          formResetField={resetField}
          errors={formState.errors}
          setValue={setValue}
          getValues={getValues}
          shouldRender={true}
          disabled={false}
          required={false}
        />
        <div>
          <FormFieldHeader
            fieldName={'Candidate Source'}
            classNames={'fw-semibold mb-2'}
            isRequired={true}
          />
          <Row>
            <Col xs={3}>
              <SourceInput
                label={'Where did you find the candidate?'}
                labelClassName={'text-dark-200 fs-6'}
                sources={props.sources}
                formControl={control}
                shouldRender={true}
                disabled={!!props.referrerUserId}
                initSourceId={initSourceId}
                required={true}
                isLoading={props.sources == null}
                extraOptions={[
                  {
                    label: 'Referrals',
                    options: [
                      { label: 'Employee Referral', value: REFERRAL_VALUE },
                    ],
                  },
                ]}
              />
            </Col>
          </Row>
          {watch('sourceId')?.toString() === REFERRAL_VALUE && (
            <div className='mt-4'>
              <ReferralKnowledge formRegister={register} />
              <FormFieldHeader
                classNames='mt-4 fs-5 fw-semibold'
                fieldName='Who gets the credit'
                isRequired={true}
              />
              <ReferralInputData
                formControl={control}
                errors={formState.errors}
                referrerUserId={props.referrerUserId}
                formRegister={register}
                watch={watch}
                getValues={getValues}
                formResetField={resetField}
                setValue={setValue}
              />
            </div>
          )}
        </div>
        <CandidateCurrentPositionInput formRegister={register} />
        <CandidateYearsExperienceInput formControl={control} />
        <HasWorkingVisaInput
          label={`The candidate has legal right to work in ${
            jobPost?.location?.country ?? 'job location'
          }?`}
          formRegister={register}
          formControl={control}
          shouldRender={true}
          required={false}
          disabled={false}
        />
        <CandidatePortfolioInput formRegister={register} />
        <CandidateDetailInput
          referralFlow={!!props.referrerUserId}
          formRegister={register}
          getValues={getValues}
        />
      </div>
    </form>
  );
}

function mapJobPostToOption(
  jobPost: JobPost,
  appliedJobPostIds: number[],
): SelectOption {
  const disabled = !appliedJobPostIds.includes(jobPost.id);

  return {
    value: jobPost.id.toString(),
    label: jobPost.name,
    subLabel: (
      <Col xs='4'>
        <Chip
          className='d-block mw-100 text-truncate fw-semibold'
          color={ChipColor.Gray}
        >
          {jobPost.location.name}
        </Chip>
      </Col>
    ),
    isDisabled: disabled,
    tooltipText:
      disabled &&
      'The candidate already has an active application for this position.',
  };
}

async function upsert(
  candidateId: number,
  applicationId: number,
  data: CandidateDto,
) {
  const upsertedApplication = await ApplicationService.upsert(
    {
      candidateId: candidateId,
      resume:
        data.resume?.id != null || data.resume?.content != null
          ? data.resume
          : null,
      coverLetter:
        data.coverLetter?.id != null || data.coverLetter?.content != null
          ? data.coverLetter
          : null,
      documents: data.documents,
      company: data.company,
      jobTitle: data.jobTitle,
      jobStageId: data.jobStageId,
      yearsExperience: data.yearsExperience,
      academicDegree: data.academicDegree,
      linkedinUrl: data.linkedinUrl,
      portfolioUrl: data.portfolioUrl,
      jobPostId: data.jobPostId,
      salaryExpectation: data.salaryExpectation,
      salaryCurrency: data.salaryCurrency,
      candidateDetail: data.candidateDetail,
      sourceId: data.sourceId,
      hasWorkingVisa: data.hasWorkingVisa,
      referralKnowledge:
        data.sourceId.toString() == REFERRAL_VALUE
          ? data.referralKnowledge
          : null,
      referrerUserId:
        data.sourceId.toString() == REFERRAL_VALUE ? data.referrerUserId : null,
      referrerEmail:
        data.sourceId.toString() == REFERRAL_VALUE ? data.referrerEmail : null,
    },
    applicationId,
  );

  return upsertedApplication.id;
}
